package org.maplibre.compose.location

import org.maplibre.compose.camera.CameraAnimation
import org.maplibre.compose.camera.CameraUpdate
import org.maplibre.compose.map.MapState
import org.maplibre.spatialk.units.Bearing
import org.maplibre.spatialk.units.extensions.inDegrees

/**
 * Convenience method for keeping [mapState] in sync with the location change that triggered this
 * [LocationTrackingEffect] callback.
 *
 * @param animation the camera transition to the new location, or `null` to move the camera without
 *   one.
 * @param updateBearing determines how the bearing affects the camera state.
 */
public suspend fun LocationChangeScope.updateCamera(
  mapState: MapState,
  animation: CameraAnimation? = CameraAnimation.Ease.Standard,
  updateBearing: BearingUpdate = BearingUpdate.TrackAutomatic,
) {
  val selectedBearing =
    when (updateBearing) {
      BearingUpdate.Ignore -> null
      BearingUpdate.AlwaysNorth -> Bearing.North
      BearingUpdate.TrackCourse -> currentLocation.course
      BearingUpdate.TrackHeading -> currentHeading?.bearing
      BearingUpdate.TrackAutomatic -> mostAccurateBearing()
    }

  val newPosition =
    mapState.cameraPosition.copy(
      center = currentLocation.position,
      bearing =
        when (updateBearing) {
          BearingUpdate.Ignore -> mapState.cameraPosition.bearing
          else ->
            selectedBearing?.let { (it - Bearing.North).inDegrees }
              ?: mapState.cameraPosition.bearing
        },
    )

  if (animation == null) mapState.setCameraPosition(newPosition)
  else
    mapState.animateCamera(
      CameraUpdate(
        center = currentLocation.position,
        bearing = selectedBearing?.let { (it - Bearing.North).inDegrees },
      ),
      animation,
    )
}

/**
 * How [updateCamera] updates camera bearing.
 *
 * Values may be added in minor releases; use an `else` branch when matching.
 */
public enum class BearingUpdate {
  /** Ignore changes in bearing and keep the current orientation. */
  Ignore,

  /** Ignore changes in bearing and reset the orientation to point north. */
  AlwaysNorth,

  /** Update camera rotation based on location course (direction of movement). */
  TrackCourse,

  /** Update camera rotation based on the device heading. */
  TrackHeading,

  /**
   * Update the camera's bearing based on the more accurate of two sources: course (direction of
   * movement) or heading (the direction that the device faces).
   */
  TrackAutomatic,
}

private fun LocationChangeScope.mostAccurateBearing(): Bearing? =
  selectMostAccurateBearing(
      currentLocation.course?.let { BearingMeasurement(it, currentLocation.courseAccuracy) },
      currentHeading?.let { BearingMeasurement(it.bearing, it.accuracy) },
    )
    ?.bearing
